import { z } from "zod/v4";
import {
  CameraSubject,
  ClientApiFeaturePathParams,
  CollectionResponse,
  SingleResponse,
  defineEndpoint,
  defineResource,
} from "@visioni/a3s-client-api";
import { isoDatetimeToDateString, stringToInt } from "@visioni/a3s-codecs";
import { FEATURE_NAME, OBJECT_TYPES } from "../constants/index";

export type ConfirmedDumpPathParams = ClientApiFeaturePathParams;

/** Waste category detected in a confirmed dump. */
export const ObjectType = z.enum(OBJECT_TYPES);
export type ObjectType = z.output<typeof ObjectType>;

/**
 * Body for confirming a dump. `timestamp` is the clip start time (the clip
 * identity). `detectedAt`/`detectedBy` are intentionally absent — the server
 * stamps them from the authenticated actor and clock, never the client.
 *
 * `description` is mandatory: reviewers must describe what was detected so the
 * customer can identify the dump in the clip, so every persisted record carries
 * reviewer detail regardless of the chosen category.
 */
export const CreateConfirmedDumpBody = z.object({
  timestamp: isoDatetimeToDateString,
  objectType: ObjectType,
  description: z.string().trim().min(1).max(500),
});

export type CreateConfirmedDumpBodyWire = z.input<typeof CreateConfirmedDumpBody>;
export type CreateConfirmedDumpBody = z.output<typeof CreateConfirmedDumpBody>;

export const ConfirmedDumpItem = z.object({
  subject: CameraSubject,
  timestamp: z.string(),
  objectType: ObjectType,
  description: z.string().optional(),
  detectedAt: z.iso.datetime(),
  detectedBy: z.string(),
});
export type ConfirmedDumpItem = z.output<typeof ConfirmedDumpItem>;

export const CreateConfirmedDumpResponse = SingleResponse(ConfirmedDumpItem);
export type CreateConfirmedDumpResponse = z.output<typeof CreateConfirmedDumpResponse>;

export const confirmedDumpsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "confirmed-dumps",
  scope: "camera",
});

export const createConfirmedDumpEndpoint = defineEndpoint(confirmedDumpsResource, {
  method: "POST",
  bodySchema: CreateConfirmedDumpBody,
  responseSchema: CreateConfirmedDumpResponse,
});

/**
 * Query params for listing a camera's confirmed dumps. `limit` bounds the page
 * (server caps at 100); `cursor` is the opaque `byCamera`-index cursor from a
 * previous page's response. No time range: the list is scoped to a single
 * camera slot and paged newest-first, so callers page with the cursor rather
 * than a window. Mirrors the pagination convention in a3s-media list clips.
 */
export const ListConfirmedDumpsQueryParams = z.object({
  limit: stringToInt.pipe(z.int().positive().max(100)).optional(),
  cursor: z.string().optional(),
});

export type ListConfirmedDumpsQueryParamsWire = z.input<typeof ListConfirmedDumpsQueryParams>;
export type ListConfirmedDumpsQueryParams = z.output<typeof ListConfirmedDumpsQueryParams>;

/**
 * Paged list of a camera's confirmed dumps, newest-first. Reuses
 * {@link ConfirmedDumpItem} as the row — the app resolves the human object
 * label (from `@visioni/a3s-dumping/constants`) and formats `detectedAt` /
 * `timestamp` in the viewer's locale itself, so the endpoint stays a pure data
 * projection with no server-side timezone dependency. `(timestamp, detectedAt)`
 * is unique per camera, so the app derives a stable React key from those.
 */
export const ListConfirmedDumpsResponse = CollectionResponse(ConfirmedDumpItem);
export type ListConfirmedDumpsResponse = z.output<typeof ListConfirmedDumpsResponse>;

export const listConfirmedDumpsEndpoint = defineEndpoint(confirmedDumpsResource, {
  responseSchema: ListConfirmedDumpsResponse,
  querySchema: ListConfirmedDumpsQueryParams,
});
